import type {
  HealthCheck, HealthColumn, HealthCopy, HealthGap, HealthRelationship, HealthTable, Scalar,
} from '@/frontend/features/Fde/dataHealth/types'
import { format, formatDate, formatNumber } from '@/frontend/features/Fde/dataHealth/types'
import { errorLabel, layerLabel, metricLabel, valueText } from '@/frontend/features/Fde/dataHealth/labels'

// Catalog of measures: each check becomes one fixed sentence built only from its numbers, plus the short
// "problem" and "limit" texts of the group tables. Templates live in config/locales/fde_data_health.pt-BR.yml
// (measure_*, part_*, limit_*). Nothing here explains causes or meaning: only what was measured.

export interface MeasureContext {
  byId: Map<string, HealthTable>
  relationships: HealthRelationship[]
  // dictionary labels used by more than one table: the sentence adds the layer to tell them apart
  ambiguous: Set<string>
  copy: HealthCopy
}

export interface CheckDescription {
  sentence: string
  problem: string
  // null when the measure has no limit worth stating (a column empty in every row)
  limit: string | null
}

export function num(value: Scalar | undefined): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) return Number(value)
  return null
}

function str(value: Scalar | undefined): string | null {
  if (typeof value === 'string' && value.trim()) return value.trim()
  if (typeof value === 'number') return String(value)
  return null
}

const share = [0, 1, 2].map((digits) => new Intl.NumberFormat('pt-BR', { maximumFractionDigits: digits }))
const tiny = new Intl.NumberFormat('pt-BR', { maximumSignificantDigits: 2 })
const amount = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

// "32%", "6,7%", "0,31%", "0,0055%", "99,97%": never rounds a part up to 100% or a non-zero part down to 0%.
export function formatShare(pct: number | null | undefined) {
  if (pct == null || !Number.isFinite(pct)) return '-'
  const abs = Math.abs(pct)
  if (abs > 0 && abs < 0.01) return `${tiny.format(pct)}%`
  const digits = abs >= 99.5 && abs < 100 ? 2 : abs >= 10 ? 0 : abs >= 1 ? 1 : 2
  return `${share[digits].format(pct)}%`
}

export function formatAmount(value: number | null | undefined) {
  return value == null || !Number.isFinite(value) ? '-' : amount.format(value)
}

function plural(copy: HealthCopy, key: string, count: number) {
  return format(copy[`${key}_${count === 1 ? 'one' : 'other'}`], { count: formatNumber(count) })
}

export const rowsText = (copy: HealthCopy, count: number) => plural(copy, 'rows', count)
export const daysText = (copy: HealthCopy, count: number) => plural(copy, 'days', count)

// "antes de 2000" for a first of January, "01/10/2026" otherwise.
function dayText(value: string) {
  return /^\d{4}-01-01$/.test(value) ? value.slice(0, 4) : formatDate(value)
}

// ---- names -------------------------------------------------------------------------------------

export function tableTechnical(id: string, table?: HealthTable) {
  return table?.table || id
}

// Sentence subject: dictionary label, or the technical name when there is no dictionary.
export function tableSubject(id: string, ctx: MeasureContext) {
  const table = ctx.byId.get(id)
  const label = table?.label?.trim()
  if (!table || !label) return tableTechnical(id, table)
  return ctx.ambiguous.has(label) ? `${label} (${layerLabel(table.layer, ctx.copy)})` : label
}

export function columnOf(table: HealthTable | undefined, name: string): HealthColumn | undefined {
  return table?.columns?.find((column) => column.column === name)
}

export function splitColumns(value: string | null | undefined) {
  return (value ?? '').split(',').map((part) => part.trim()).filter(Boolean)
}

// "Data do pedido (order_date)", or "order_date" without a dictionary.
export function columnText(table: HealthTable | undefined, name: string, copy: HealthCopy) {
  const label = columnOf(table, name)?.label?.trim()
  return label && label !== name ? format(copy.column_named, { label, name }) : name
}

function columnsText(table: HealthTable | undefined, names: string[], copy: HealthCopy) {
  return names.map((name) => columnText(table, name, copy)).join(', ')
}

export function partitionText(check: HealthCheck, table: HealthTable | undefined, copy: HealthCopy) {
  return check.partition ? `${table?.partition_column ?? copy.partition} ${check.partition}` : ''
}

// ---- limits ------------------------------------------------------------------------------------

const DAY_METRICS = new Set(['days_since_last', 'partition_days_since_last', 'max_gap_days', 'volume_anomaly_days', 'snapshot_missing_days'])
const ROW_METRICS = new Set(['duplicate_keys', 'date_out_of_range', 'row_count'])

function amountText(metric: string, value: number, copy: HealthCopy) {
  if (metric.endsWith('_pct')) return formatShare(value)
  if (DAY_METRICS.has(metric)) return daysText(copy, value)
  if (ROW_METRICS.has(metric)) return rowsText(copy, value)
  return formatNumber(value)
}

// "até 1% sem par", "até 2 dias", "0", "pelo menos 1 linha".
export function limitFor(check: HealthCheck, copy: HealthCopy): string | null {
  const { metric, threshold } = check
  if (metric === 'column_always_empty' || threshold == null) return null
  if (metric === 'match_pct') {
    const allowed = Math.max(0, 100 - threshold)
    return allowed === 0 ? copy.limit_zero : format(copy.limit_unmatched, { value: formatShare(allowed) })
  }
  if (check.op === '<=' && threshold === 0) return copy.limit_zero
  return format(check.op === '<=' ? copy.limit_at_most : copy.limit_at_least, { value: amountText(metric, threshold, copy) })
}

// ---- parts: "n de N linhas (x%)" ---------------------------------------------------------------

// Value with the problem count of each "part of the rows" metric.
const PART_KEYS: Record<string, string | null> = {
  key_blank_pct: 'blank', key_null_pct: 'nulls', column_always_empty: 'nulls', date_null_pct: 'nulls',
  negative_pct: 'negatives', outlier_pct: 'outliers', match_pct: 'unmatched', rule_violation_pct: 'violations',
  duplicate_keys: 'duplicates', value_not_allowed_pct: 'not_allowed', date_out_of_range: null,
}

interface Part { count: number; total: number | null }

function partOf(check: HealthCheck): Part | null {
  if (!(check.metric in PART_KEYS)) return null
  const values = check.values ?? {}
  const key = PART_KEYS[check.metric]
  let count = key ? num(values[key]) : null
  // these two measure the count itself
  if (count == null && (check.metric === 'date_out_of_range' || check.metric === 'duplicate_keys')) count = check.value
  if (count == null) return null
  return { count, total: num(values.rows) }
}

function pctOf(part: Part) {
  return part.total ? (part.count / part.total) * 100 : 0
}

// With the row noun: "1.234 de 3.850 linhas (32%)"; without a base: "3 linhas".
function partRows(part: Part, copy: HealthCopy, withPct = true) {
  if (part.total == null) return rowsText(copy, part.count)
  const key = `${withPct ? 'part_rows' : 'part_rows_plain'}_${part.total === 1 ? 'one' : 'other'}`
  return format(copy[key], { count: formatNumber(part.count), total: formatNumber(part.total), pct: formatShare(pctOf(part)) })
}

// Table cell: "1.234 de 3.850 (32%)".
function partShort(part: Part, copy: HealthCopy) {
  if (part.total == null) return rowsText(copy, part.count)
  return format(copy.part_short, { count: formatNumber(part.count), total: formatNumber(part.total), pct: formatShare(pctOf(part)) })
}

function partDays(count: number, total: number | null, copy: HealthCopy) {
  if (total == null) return daysText(copy, count)
  return format(copy[`part_days_${total === 1 ? 'one' : 'other'}`], { count: formatNumber(count), total: formatNumber(total) })
}

// ---- helpers per metric ------------------------------------------------------------------------

function longestGap(check: HealthCheck, table: HealthTable | undefined): HealthGap | null {
  const own = check.detail?.gaps
  const gaps = own?.length
    ? own
    : check.partition
      ? table?.partitions?.find((part) => part.value === check.partition)?.gaps ?? []
      : table?.gaps ?? []
  return gaps.reduce<HealthGap | null>((best, gap) => (!best || gap.days > best.days ? gap : best), null)
}

// Codes outside the connector list: detail.codes, or values.examples "J=4943|K=12".
export function notAllowedCodes(check: HealthCheck): { value: string; rows: number | null }[] {
  if (check.detail?.codes?.length) return check.detail.codes
  const text = str(check.values?.examples)
  if (!text) return []
  return text.split('|').map((item) => {
    const at = item.lastIndexOf('=')
    return at < 0 ? { value: item.trim(), rows: null } : { value: item.slice(0, at).trim(), rows: num(item.slice(at + 1)) }
  }).filter((item) => item.value)
}

// values.to of a relationship check: "<table id>.<cols>".
function parseTarget(to: string | null, ctx: MeasureContext) {
  if (!to) return null
  let best = ''
  for (const id of ctx.byId.keys()) if (to.startsWith(`${id}.`) && id.length > best.length) best = id
  if (best) return { table: best, columns: splitColumns(to.slice(best.length + 1)) }
  const at = to.lastIndexOf('.')
  return at < 0 ? { table: to, columns: [] } : { table: to.slice(0, at), columns: splitColumns(to.slice(at + 1)) }
}

export function relationshipOf(check: HealthCheck, ctx: MeasureContext) {
  const target = parseTarget(str(check.values?.to), ctx)
  const columns = splitColumns(check.column)
  const relationship = ctx.relationships.find((item) =>
    item.from.table === check.table
    && (!target || item.to.table === target.table)
    && (!columns.length || columns.every((column) => item.from.columns.includes(column))))
  return {
    relationship,
    fromColumns: columns.length ? columns : relationship?.from.columns ?? [],
    toTable: target?.table ?? relationship?.to.table ?? null,
    toColumns: target?.columns.length ? target.columns : relationship?.to.columns ?? [],
  }
}

export function outlierRange(check: HealthCheck, table: HealthTable | undefined) {
  const outliers = check.column ? columnOf(table, check.column)?.outliers : null
  const low = check.detail?.low ?? outliers?.low ?? null
  const high = check.detail?.high ?? outliers?.high ?? null
  return { low, high }
}

// ---- the sentence ------------------------------------------------------------------------------

export function describeCheck(check: HealthCheck, ctx: MeasureContext): CheckDescription {
  const { copy } = ctx
  const table = ctx.byId.get(check.table)
  const values = check.values ?? {}
  const partition = partitionText(check, table, copy)
  const tableName = partition ? format(copy.measure_subject_column, { table: tableSubject(check.table, ctx), column: partition }) : tableSubject(check.table, ctx)
  const columns = splitColumns(check.column)
  const column = columnsText(table, columns, copy)
  const subject = column ? format(copy.measure_subject_column, { table: tableName, column }) : tableName
  const limit = limitFor(check, copy)
  const done = (text: string, problem: string): CheckDescription => ({
    sentence: limit == null ? text : `${text} ${format(copy.measure_limit, { limit })}`,
    problem,
    limit,
  })

  if (check.value == null) {
    const error = errorLabel(check.error, copy) ?? copy.measure_no_value
    return { sentence: format(copy.measure_error, { subject, error }), problem: error, limit }
  }

  const value = check.value
  const generic = (problem = valueText(check.metric, value, copy)) =>
    done(format(copy.measure_generic, { subject, metric: metricLabel(check.metric, copy), value: problem }), problem)
  const part = partOf(check)
  const pair = (right: string | null) => {
    const left = right ? `${tableName} × ${tableSubject(right, ctx)}` : tableName
    return column ? format(copy.measure_subject_column, { table: left, column }) : left
  }

  switch (check.metric) {
    case 'match_pct': {
      if (!part || part.total == null) return generic(format(copy.problem_unmatched, { value: formatShare(100 - value) }))
      const link = relationshipOf(check, ctx)
      const toTable = link.toTable ? ctx.byId.get(link.toTable) : undefined
      const target = link.toTable
        ? link.toColumns.length
          ? `${tableSubject(link.toTable, ctx)} (${columnsText(toTable, link.toColumns, copy)})`
          : tableSubject(link.toTable, ctx)
        : copy.measure_other_table
      return done(format(copy.measure_match_pct, {
        table: tableName, part: partRows(part, copy), column: columnsText(table, link.fromColumns, copy) || copy.measure_key, target,
      }), partShort(part, copy))
    }
    case 'column_always_empty':
      if (!part || part.total == null) return generic()
      return done(format(copy.measure_column_always_empty, { table: tableName, column: column || copy.measure_column, part: partRows(part, copy, false) }), partShort(part, copy))
    case 'date_out_of_range': {
      if (!part) return generic()
      const min = str(values.min_day) ?? check.detail?.min_day ?? null
      const max = str(values.max_day) ?? check.detail?.max_day ?? null
      const dateColumn = column || (table?.date_column ? columnText(table, table.date_column, copy) : copy.measure_date)
      const rule = min && max
        ? format(copy.rule_before_after, { min: dayText(min), max: dayText(max) })
        : min ? format(copy.rule_before, { min: dayText(min) }) : max ? format(copy.rule_after, { max: dayText(max) }) : null
      return done(format(rule ? copy.measure_date_out_of_range : copy.measure_date_out_of_range_norule, {
        table: tableName, part: partRows(part, copy), column: dateColumn, rule: rule ?? '',
      }), partShort(part, copy))
    }
    case 'key_blank_pct':
    case 'key_null_pct':
    case 'date_null_pct':
    case 'negative_pct':
    case 'rule_violation_pct':
    case 'value_not_allowed_pct': {
      if (!part || part.total == null) return generic(formatShare(value))
      const fallbackColumn = check.metric === 'date_null_pct'
        ? (table?.date_column ? columnText(table, table.date_column, copy) : copy.measure_date)
        : check.metric.startsWith('key_') && table?.key?.length ? columnsText(table, table.key, copy) : copy.measure_column
      const codes = notAllowedCodes(check)
      const codesText = codes.slice(0, 5).map((code) => (code.rows == null ? code.value : `${code.value}: ${formatNumber(code.rows)}`)).join(', ')
      const key = check.metric === 'value_not_allowed_pct' && !codesText ? 'measure_value_not_allowed_pct_nocodes' : `measure_${check.metric}`
      return done(format(copy[key], {
        table: tableName, subject, part: partRows(part, copy), column: column || fallbackColumn,
        rule: check.name || check.id, codes: codes.length > 5 ? `${codesText}, …` : codesText,
      }), partShort(part, copy))
    }
    case 'outlier_pct': {
      if (!part || part.total == null) return generic(formatShare(value))
      const { low, high } = outlierRange(check, table)
      const ranged = low != null && high != null
      return done(format(ranged ? copy.measure_outlier_pct : copy.measure_outlier_pct_norange, {
        table: tableName, part: partRows(part, copy), column: column || copy.measure_column,
        low: ranged ? formatNumber(low) : '', high: ranged ? formatNumber(high) : '',
      }), partShort(part, copy))
    }
    case 'duplicate_keys': {
      if (!part) return generic()
      const keyColumns = table?.duplicate_keys?.key?.length ? table.duplicate_keys.key : table?.key?.length ? table.key : columns
      return done(format(copy.measure_duplicate_keys, {
        table: tableName, part: partRows(part, copy), key: columnsText(table, keyColumns, copy) || copy.measure_key,
      }), partShort(part, copy))
    }
    case 'days_since_last':
    case 'partition_days_since_last': {
      const last = str(values.last_date) ?? (check.partition ? null : table?.last_date ?? null)
      const days = daysText(copy, value)
      return done(format(last ? copy.measure_days_since_last : copy.measure_days_since_last_nodate, {
        table: tableName, date: last ? formatDate(last) : '', days,
      }), days)
    }
    case 'max_gap_days': {
      const gap = longestGap(check, table)
      const days = daysText(copy, value)
      return done(format(gap ? copy.measure_max_gap_days : copy.measure_max_gap_days_norange, {
        table: tableName, days, from: gap ? formatDate(gap.from) : '', to: gap ? formatDate(gap.to) : '',
      }), days)
    }
    case 'volume_anomaly_days': {
      const text = partDays(value, num(values.window_days), copy)
      return done(format(copy.measure_volume_anomaly_days, { table: tableName, part: text }), text)
    }
    case 'snapshot_missing_days': {
      const text = partDays(value, num(values.days), copy)
      const from = str(values.first_date)
      const to = str(values.last_date)
      return done(format(from && to ? copy.measure_snapshot_missing_days : copy.measure_snapshot_missing_days_norange, {
        table: tableName, part: text, from: from ? formatDate(from) : '', to: to ? formatDate(to) : '',
      }), text)
    }
    case 'snapshot_rows_drop_pct': {
      const lastRows = num(values.last_rows)
      const median = num(values.median_rows)
      const date = str(values.last_date)
      const problem = format(copy.problem_drop, { pct: formatShare(value) })
      if (lastRows == null || median == null || !date) return generic(problem)
      const snapshots = num(values.snapshots)
      return done(format(copy.measure_snapshot_rows_drop_pct, {
        table: tableName, date: formatDate(date), last: rowsText(copy, lastRows), median: rowsText(copy, median),
        snapshots: snapshots == null ? '-' : formatNumber(snapshots), pct: formatShare(value),
      }), problem)
    }
    case 'layer_row_diff_pct': {
      const left = num(values.left_rows)
      const right = num(values.right_rows)
      const problem = format(copy.problem_diff, { pct: formatShare(value) })
      if (left == null || right == null) return generic(problem)
      return done(format(copy.measure_layer_row_diff_pct, {
        pair: pair(str(values.right)), left: rowsText(copy, left), right: rowsText(copy, right), pct: formatShare(value),
      }), problem)
    }
    case 'layer_value_diff_pct':
    case 'reconciliation_diff_pct': {
      const left = num(values.left_value)
      const right = num(values.right_value)
      const problem = format(copy.problem_diff, { pct: formatShare(value) })
      if (left == null || right == null) return generic(problem)
      return done(format(copy.measure_value_diff_pct, {
        pair: pair(str(values.right)), left: formatAmount(left), right: formatAmount(right), pct: formatShare(value),
      }), problem)
    }
    case 'row_count': {
      const rows = rowsText(copy, value)
      return done(format(copy.measure_row_count, { table: tableName, rows }), rows)
    }
    case 'connector_check': {
      const text = formatNumber(value)
      return done(format(copy.measure_connector_check, { subject, name: check.name || check.id, value: text }), text)
    }
    default:
      return generic()
  }
}

// Where in the table the check looked: column(s), partition or rule name.
export function checkWhere(check: HealthCheck, table: HealthTable | undefined, copy: HealthCopy) {
  return [
    ...splitColumns(check.column).map((name) => ({ kind: 'column' as const, name })),
    ...(check.partition ? [{ kind: 'text' as const, name: partitionText(check, table, copy) }] : []),
    ...(check.name && check.metric !== 'reconciliation_diff_pct' ? [{ kind: 'text' as const, name: check.name }] : []),
  ]
}
